import { useState } from 'react'
import { InfoButton } from 'components/buttons'
import { Form, Input } from 'components/forms'
import classNames from 'classnames'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { track } from 'lib/analytics'
import { registryAddItemsPath } from 'lib/urls'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import Slide, { Title } from '../../Slide/Slide'
import css from './AddPartner.scss'
import * as api from '../../../api'
import { useNavigate } from 'react-router-dom-v5-compat'

interface AddPartnerProps {
  nextPath: string
}

export const AddPartner = ({ nextPath }: AddPartnerProps) => {
  const [currentUser] = useCurrentUser()
  const [submitting, setSubmitting] = useState(false)
  const [showPartnerForm, setShowPartnerForm] = useState<boolean>()
  const [showPartnerFormEmailHelperText, setShowPartnerFormEmailHelperText] =
    useState(false)
  const [formError, setFormError] = useState<string>()

  const { register, handleSubmit, errors } = useForm()
  const registry = useSelector((state: any) => state.registry)
  const navigate = useNavigate()

  const goToNext = () => {
    if (nextPath === registryAddItemsPath) {
      window.location.href = nextPath // This is a different page and not part of the onboarding Wizard
    } else {
      navigate(nextPath)
    }
  }

  const onSubmit = (data: any) => {
    setSubmitting(true)

    const partner = showPartnerForm
      ? {
          firstName: data.partnerFirstName,
          lastName: data.partnerLastName,
          email: data.partnerEmailAddress,
        }
      : null

    const registryId = registry?.id || currentUser?.currentRegistry?.id

    api
      .finishingTouches({ registryId, partner })
      .then(() => {
        setSubmitting(false)
        setFormError(undefined)
        track.onboardingQuestionAnswered({
          eventLocation:
            track.EventLocation.ONBOARDING_MINUS_PARTNER_EMAIL_COLLECTION,
        })
        if (showPartnerForm)
          track.collaboratorInvited({
            collaboratorRole: track.CollaboratorRole.PARTNER,
          })
        goToNext()
      })
      .catch((error) => {
        setSubmitting(false)
        setFormError(error._error)
      })
  }

  const validateEmail = (email: string): string | undefined => {
    if (email.toLowerCase() === currentUser?.email?.toLowerCase()) {
      return "Whoops! You can't add yourself!"
    }
    return undefined
  }

  return (
    <Slide className={css.AddPartner__slide}>
      <div className={css.AddPartner__container}>
        <div className={css.AddPartner__headerContainer}>
          <Title className={css.AddPartner__title}>
            Your registry has been created!
          </Title>
          <p className={css.AddPartner__subTitle}>Let's get you set up.</p>
        </div>
        <Form
          className={css.AddPartner__form}
          error={formError}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={css.AddPartner__formFieldsContainer}>
            <div className={css.AddPartner__questionContainer}>
              <p className={css.AddPartner__question}>
                Do you have a partner with you on this journey?
              </p>
              <div className={css.AddPartner__buttonContainer}>
                <InfoButton
                  className={classNames(css.AddPartner__button, {
                    [css.AddPartner__button__active]: showPartnerForm,
                    [css.AddPartner__button__inactive]:
                      showPartnerForm === false,
                  })}
                  type="button"
                  onClick={() => setShowPartnerForm(true)}
                >
                  Yes
                </InfoButton>
                <InfoButton
                  className={classNames(css.AddPartner__button, {
                    [css.AddPartner__button__active]: showPartnerForm === false,
                    [css.AddPartner__button__inactive]: showPartnerForm,
                  })}
                  type="button"
                  onClick={() => setShowPartnerForm(false)}
                >
                  No
                </InfoButton>
              </div>
            </div>
            <div
              className={classNames(css.AddPartner__inputContainer, {
                [css.AddPartner__inputContainer__show]: showPartnerForm,
              })}
            >
              <p className={css.AddPartner__inputContainerSubtext}>
                Add their name to help friends and family find your registry.
              </p>
              <Input
                error={errors.partnerFirstName && 'First name is required.'}
                inputRef={register({ required: showPartnerForm })}
                label="Partner's first name"
                name="partnerFirstName"
                type="text"
              />
              <Input
                error={errors.partnerLastName && 'Last name is required.'}
                inputRef={register({ required: showPartnerForm })}
                label="Partner's last name"
                name="partnerLastName"
                type="text"
              />
            </div>
            <div
              className={classNames(css.AddPartner__inputContainer, {
                [css.AddPartner__inputContainer__show]: showPartnerForm,
              })}
            >
              <p className={css.AddPartner__inputContainerSubtext}>
                Send them an invite to help build out your registry.
              </p>
              <Input
                error={
                  errors.partnerEmailAddress &&
                  errors.partnerEmailAddress.message
                }
                help={
                  showPartnerFormEmailHelperText
                    ? 'Email address is required for them to add items to your registry.'
                    : ''
                }
                inputRef={register({
                  validate: (email: string) => validateEmail(email),
                })}
                label="Email (optional)"
                name="partnerEmailAddress"
                type="email"
                onBlur={() => setShowPartnerFormEmailHelperText(false)}
                onFocus={() => setShowPartnerFormEmailHelperText(true)}
              />
            </div>
          </div>
          <div className={css.AddPartner__submitButtonContainer}>
            <InfoButton
              block
              pill
              className={css.AddPartner__submitButton}
              disabled={submitting}
              submitting={submitting}
              type="submit"
            >
              Next
            </InfoButton>
          </div>
        </Form>
      </div>
    </Slide>
  )
}
